import React, {useState, useContext, useEffect} from "react";
import {Box, useMediaQuery, useTheme, Stack, Typography, IconButton, Tooltip, Button} from "@mui/material";
import {useAtom, useAtomValue, useSetAtom} from "jotai";
import {HugeiconsIcon} from "@hugeicons/react";
import {DatabaseIcon, Activity01Icon, Layers01Icon, Bookmark01Icon, Refresh01Icon, Add01Icon} from "@hugeicons/core-free-icons";
import {Context} from "../../../context/AppContext";
import {MobileUnavailable} from "../../../components/MobileUnavailable";
import {MCPServerListPanelNew} from "./components/MCPServerListPanelNew";
import {MCPServerDetailsPanelNew} from "./components/MCPServerDetailsPanelNew";
import {AddMCPServerModalNew} from "./components/AddMCPServerModalNew";
import {Panel} from "../../../pages/registry-project/dashboard/components/Panel";
import {ServiceNotConfiguredBanner} from "../components/ServiceNotConfiguredBanner";
import {
    projectAtom,
    subProjectAtom,
    flowUidAtom,
    serversAtom,
    selectedServerAtom,
    mcpLoadingAtom,
    errorAtom
} from "./atoms";
import {toast} from "react-toastify";
import {useParams} from "react-router-dom";

// StatTile component - exactly like RegistryMessageAnnotationsPage
const StatTile = ({icon, color, label, value, note}) => (
  <Panel sx={{p: 2, flexDirection: "row", alignItems: "center", gap: 1.5}}>
    <Box sx={{
      width: 40, height: 40, borderRadius: "12px", flexShrink: 0, display: "flex", alignItems: "center",
      justifyContent: "center", color, bgcolor: `${color}1F`,
    }}>
      <HugeiconsIcon icon={icon} size={19}/>
    </Box>
    <Box sx={{minWidth: 0}}>
      <Typography noWrap sx={{fontSize: 13, fontWeight: 500, color: "#6B6B80"}}>{label}</Typography>
      <Typography sx={{fontSize: 24, fontWeight: 700, lineHeight: 1.2, letterSpacing: "-0.02em", color: "#23264D"}}>
        {value}
      </Typography>
      {note && <Typography noWrap sx={{fontSize: 12, color: "#8A8AA0"}}>{note}</Typography>}
    </Box>
  </Panel>
);

export const MCPDiscoveryPageRegistry = ({project, subProject}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const {serverId} = useParams();

    const [servers, setServers] = useAtom(serversAtom);
    const [, setSelectedServer] = useAtom(selectedServerAtom);
    const [loading, setLoading] = useAtom(mcpLoadingAtom);
    const [, setError] = useAtom(errorAtom);
    const setProjectAtom = useSetAtom(projectAtom);
    const setSubProjectAtom = useSetAtom(subProjectAtom);
    const selectedFlowUid = useAtomValue(flowUidAtom);

    const {state, getAllServersWithDetails} = useContext(Context);

    const [openAdd, setOpenAdd] = useState(false);

    // Sync route-driven project/language into atoms so child components can read them without prop drilling
    useEffect(() => {
        setProjectAtom(project);
    }, [project, setProjectAtom]);

    useEffect(() => {
        setSubProjectAtom(subProject);
    }, [subProject, setSubProjectAtom]);

    useEffect(() => {
        if (state?.mcpServers) {
            setServers(state.mcpServers);
            if (serverId) {
                const server = state.mcpServers.find(s => s.id === serverId);
                if (server) {
                    setSelectedServer(server);
                }
            }
        }
    }, [state?.mcpServers, setServers, serverId, setSelectedServer]);

    useEffect(() => {
        if (subProject?.flowUid && project?.backendConfiguration?.mcpRegistry) {
            fetchServers();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subProject?.flowUid, project?.backendConfiguration?.mcpRegistry]);

    useEffect(() => {
        return () => setSelectedServer(null);
    }, [setSelectedServer]);

    const fetchServers = async () => {
        try {
            setLoading(true);
            setError(null);
            await getAllServersWithDetails({flowUid: subProject.flowUid, projectId: project.id, subProjectId: subProject.id});
            setSelectedServer(null);
        } catch (err) {
            setError(err.message);
            toast.error("Failed to load MCP servers");
            console.error("Error fetching servers:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleServerAdded = async () => {
        setOpenAdd(false);
        await fetchServers();
    };

    if (isMobile) {
        return <MobileUnavailable/>;
    }

    const scopedServers = selectedFlowUid ? servers.filter(s => s.flow_uid === selectedFlowUid) : servers;
    const totalServers = scopedServers.length;
    const activeServers = scopedServers.filter(s => s.active).length;
    const totalTools = scopedServers.reduce((sum, s) => sum + (s.tools?.length || 0), 0);
    const totalPrompts = scopedServers.reduce((sum, s) => sum + Object.keys(s.prompts || {}).length, 0);

    return (
        <Stack gap={5} sx={{
            p: {xs: 2, md: 3}, maxWidth: {xs: "calc(100% - 16px)", md: "calc(100% - 128px)"}, width: "100%", mx: "auto",
            boxSizing: "border-box", height: {xs: "auto", md: "100%"}, minHeight: 0,
        }}>
            {/* Add MCP Server Modal */}
            <AddMCPServerModalNew
                open={openAdd}
                onClose={() => setOpenAdd(false)}
                onSuccess={handleServerAdded}
            />

            {/* Heading - exactly like RegistryMessageAnnotationsPage */}
            <Stack direction={{xs: "column", md: "row"}} justifyContent={"space-between"} alignItems={{md: "flex-end"}} gap={2}>
                <Box>
                    <Typography sx={{fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", color: "#8491F7", textTransform: "uppercase"}}>
                        MCP INTEGRATION
                    </Typography>
                    <Typography component={"h1"} sx={{fontSize: 26, fontWeight: 700, color: "#23264D", letterSpacing: "-0.02em", mt: 0.5}}>
                        Server Registry
                    </Typography>
                    <Typography sx={{fontSize: 14, color: "#6B6B80", mt: 0.5}}>
                        Discover, configure and monitor MCP servers and their capabilities
                    </Typography>
                </Box>
                <Stack direction="row" gap={1} alignItems="center">
                    <Button
                        size="small"
                        startIcon={<HugeiconsIcon icon={Add01Icon} size={16}/>}
                        onClick={() => setOpenAdd(true)}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: 13,
                            borderRadius: '10px',
                            color: '#374151',
                            bgcolor: '#FAFAFD',
                            '&:hover': {bgcolor: '#ECECF3'},
                        }}
                    >
                        Add Server
                    </Button>
                    <Tooltip title={"Refresh"}>
                        <IconButton onClick={fetchServers} disabled={loading} sx={{borderRadius: "50% !important", color: "#8A8AA0"}}>
                            <HugeiconsIcon icon={Refresh01Icon} size={17}/>
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Stack>

            {!project?.backendConfiguration?.mcpRegistry && (
                <ServiceNotConfiguredBanner label={"MCP Registry"}/>
            )}

            {/* Main layout: server list spans the full height as a sidebar, alongside the stats row and details panel */}
            <Box sx={{
                display: "grid",
                gridTemplateColumns: {xs: "1fr", md: "380px 1fr"},
                gridTemplateRows: {xs: "auto auto auto", md: "auto 1fr"},
                gridTemplateAreas: {
                    xs: `"stats" "sidebar" "details"`,
                    md: `"sidebar stats" "sidebar details"`,
                },
                gap: 3,
                flex: 1,
                minHeight: {xs: "calc(100vh - 260px)", md: 0},
            }}>
                {/* LEFT PANEL: Server List (spans full height) */}
                <Box sx={{gridArea: "sidebar", display: "flex", flexDirection: "column", overflow: "hidden", bgcolor: "#fff", border: "1px solid #ECECF3", borderRadius: "14px", minWidth: 0, minHeight: 0}}>
                    <MCPServerListPanelNew
                        onAddClick={() => setOpenAdd(true)}
                        onRefresh={fetchServers}
                    />
                </Box>

                {/* Statistics */}
                <Box sx={{gridArea: "stats", display: "grid", gap: 2.5, gridTemplateColumns: {xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))"}}}>
                    <StatTile icon={DatabaseIcon} color={"#8491F7"} label={"Total Servers"} value={totalServers}
                              note={`${activeServers} active · ${totalServers - activeServers} inactive`}/>
                    <StatTile icon={Activity01Icon} color={"#2F9A6A"} label={"Active"} value={activeServers}
                              note={totalServers ? `${Math.round((activeServers / totalServers) * 100)}% of total servers` : "No servers yet"}/>
                    <StatTile icon={Layers01Icon} color={"#D9A13A"} label={"Tools Available"} value={totalTools}
                              note={"Callable from the Tools tab"}/>
                    <StatTile icon={Bookmark01Icon} color={"#3D86C6"} label={"Prompts Available"} value={totalPrompts}
                              note={"Reusable prompt templates"}/>
                </Box>

                {/* RIGHT PANEL: Server Details */}
                <Box sx={{gridArea: "details", display: "flex", flexDirection: "column", overflow: "hidden", bgcolor: "#fff", border: "1px solid #ECECF3", borderRadius: "14px", minWidth: 0, minHeight: 0}}>
                    <MCPServerDetailsPanelNew/>
                </Box>
            </Box>
        </Stack>
    );
};
